import React from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { formatJst } from "../lib/date";
import { profileLabelForRole } from "../lib/accessProfiles";
import { formatAuditAction, formatAuditModule } from "../lib/auditFormat";
import { useLocalization } from "../hooks/useLocalization";

/** Detail drawer for a single audit event. Only ever fed a `detail` object already
 * authorized+returned by GET /audit/{id} (server-side scope check already happened) - this
 * component makes no further access decisions, it only renders. */
export const AuditDetailDrawer = ({ detail, open, onOpenChange }) => {
  const { t } = useLocalization();
  if (!detail) return null;

  const rows = [
    [t("audit.actor"), detail.actor_name || profileLabelForRole(detail.actor_role, t)],
    [t("audit.employee"), detail.employee_name],
    [t("audit.store"), detail.store_location],
    [t("audit.target"), detail.target_type],
    [t("audit.reason"), detail.reason],
    [t("audit.previousValue"), detail.previous_value],
    [t("audit.newValue"), detail.new_value],
    [t("audit.ipAddress"), detail.ip_address],
  ].filter(([, value]) => value);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="audit-detail-drawer" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("audit.detailTitle")}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          <div>
            <p className="text-lg font-semibold text-gray-900">{formatAuditAction(detail.action, t)}</p>
            <p className="text-sm text-gray-500">{formatJst(detail.timestamp_jst)}</p>
            <div className="flex gap-2 mt-2">
              <Badge variant="secondary" data-testid="audit-detail-module">{formatAuditModule(detail.module, t)}</Badge>
              <Badge variant={detail.result === "DENIED" ? "destructive" : "secondary"} data-testid="audit-detail-result">
                {detail.result === "DENIED" ? t("audit.denied") : t("audit.success")}
              </Badge>
            </div>
          </div>
          <dl className="divide-y divide-gray-100 border-t border-gray-100">
            {rows.map(([label, value]) => (
              <div key={label} className="grid grid-cols-3 gap-2 py-2 text-sm">
                <dt className="text-gray-500">{label}</dt>
                <dd className="col-span-2 text-gray-900 break-words">{String(value)}</dd>
              </div>
            ))}
          </dl>
          {detail.related_link && (
            <Link
              to={detail.related_link}
              data-testid="audit-detail-related-link"
              className="inline-block px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
              onClick={() => onOpenChange(false)}
            >
              {t("audit.goToRecord")}
            </Link>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
